import { UploadFile } from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Stack,
	TextareaAutosize,
	Typography,
} from "@mui/material";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { deleteAttachedFile, loadFileInfo } from "../../../actions/form";
import { createMessage } from "../../../actions/util";
import getBackendURL from "../../../api/api";
import { User } from "../../../auth";
import { download, request } from "../../../request";
import { ESRState } from "../../../types/data";

import { DataAction } from "../../../types/action";
import UserTreeView from "../../shared/UserTreeView";

type Props = {
	user: User;
};

export const NewThreadEntry: FC<Props> = ({ user }) => {
	const dispatch = useDispatch();
	const { esrId = "", activeLabel = "", activeId = "" } = useParams();
	const filters = useSelector((state: ESRState) => state.drawer.filters);
	// Drafts are per user as well as per ESR: users sharing a browser
	// shouldn't find someone else's unsent message filled in
	const [description, setDescription] = useLocalStorage(
		`thread-description-${user.email}-${esrId}`,
		"",
	);
	const [message, setMessage] = useLocalStorage(
		`thread-message-${user.email}-${esrId}`,
		"",
	);

	// User list management
	const request_info_from_sponsor: boolean = useSelector(
		(state: ESRState) => state.request_info_from_sponsor,
	);
	type RecipientOption = [string, string, string, string, string[], boolean];
	const [recipientOptions, setRecipientOptions] = useState<RecipientOption[]>(
		[],
	);
	const [recipients, setRecipients] = useState<string[]>([]);
	// const [groupOptions, setGroupOptions] = useState<string[]>([]);
	const [groups, setGroups] = useState<string[]>([]);
	const [allowed_user_groups, setAllowedGroups] = useState<string[]>([]);
	const [selected_user_groups, setSelectedUserGroups] = useState<string[]>([]);

	const groupLabelToValue: Record<string, string> = {
		"RSU Supervisor": "RSU",
		"NRU Supervisor": "NRU",
		"CRU Supervisor": "CRU",
		"SIUC Supervisor": "D7,8,9-SIUC",
		// "SIUC Wetlands": "SIUC-Wetlands",
		// "SIUC Biological Surveys": "SIUC-Biological Surveys",
		"NIU Supervisor": "D1,2,3-NIU",
		// "NIU Wetlands": "NIU-Wetlands",
		// "NIU Biological Surveys": "NIU-Biological Surveys",
		"UIUC Supervisor": "D4,5,6-UIUC",
		// "UIUC Wetlands": "UIUC-Wetlands",
		// "UIUC Biological Surveys": "UIUC-Biological Surveys",
		"NRU Staff District X": "NRU District X",
		"Local Roads District X County X": "Local Roads District X County X",
		"Local Roads District X Supervisor": "Local Roads District X",
	};
	let groupValueToLabel: Record<string, string> = {};
	Object.entries(groupLabelToValue).forEach(
		([key, value]) => (groupValueToLabel[value] = key),
	);

	const recipientHierarchy: Record<string, string[]> = {
		Admin: [],
		"Regulated Substances (RSU)": [
			"RSU Supervisor",
			"RSU Phase I District X",
			"RSU Phase II District X",
			"RSU District 1 Consultant",
			"RSU CDB/CI District X",
		],
		"Natural Resources (NRU)": [
			"NRU Supervisor",
			"NRU Staff District X",
			"NRU Aeronautics",
		],
		"Cultural Resources (CRU)": [
			"CRU Supervisor",
			"CRU Archaeology",
			"CRU Architecture District X Role X",
		],
		"Local Roads District X": [
			"Local Roads District X Staff",
			"Local Roads District X Supervisor",
		],
		"Local Roads Central Office District X": [],
		"District X Environmental Coordinator": [],
		Aeronautics: [],
		Railroads: [],
		"CDB/CI": [],
		Sponsor: [],
		// "INHS": [
		// 	"INHS Supervisor",
		// 	"INHS Wetlands",
		// 	"INHS Biological Surveys",
		// ],
		"D1,2,3-NIU": ["NIU Supervisor", "NIU-Wetlands", "NIU-Biological Surveys"],
		"D4,5,6-UIUC": [
			"UIUC Supervisor",
			"UIUC-Wetlands",
			"UIUC-Biological Surveys",
		],
		"D7,8,9-SIUC": [
			"SIUC Supervisor",
			"SIUC-Wetlands",
			"SIUC-Biological Surveys",
		],
		ISGS: [
			// "ISGS Wetlands",
			// "ISGS PESA",
		],
		ISWS: [],
		ISAS: [],
	};
	// Flattened key + nested-child order, used to sort the recipient tree so
	// entries appear in the same order they're listed here, when present.
	const groupOrder: string[] = Object.entries(recipientHierarchy).flatMap(
		([key, children]) => [key, ...children],
	);

	useEffect(() => {
    if (!activeId) return;

    request<{
        users: Record<
            string,
            Array<{
                email: string;
                first_name: string;
                last_name: string;
                admin: boolean;
            }>
        >;
        groups: string[];
        allowed_groups: string[];
    }>(getBackendURL(`/esr/permitted_recipients/${activeId}`)).then(
        ({ data }) => {
            let options: RecipientOption[] = Object.entries(data.users)
                .flatMap(([groupName, users]) =>
                    users.map(
                        (u) =>
                            [
                                u.email,
                                u.first_name,
                                u.last_name,
								groupName, // organization/display group
								[groupValueToLabel[groupName] ?? groupName],
                                u.admin, // API doesn't currently provide is_sponsor
                            ] as RecipientOption,
                    ),
                )
                .filter(([email]) => email !== user?.email);

            // if (request_info_from_sponsor) {
            //     options = options.filter(
            //         ([, , , , , is_sponsor]) => is_sponsor,
            //     );
            // }

            setRecipientOptions(options);

            if (request_info_from_sponsor) {
                setAllowedGroups(["Sponsor"]);
            } else {
                setAllowedGroups(
                    data.allowed_groups.map(
                        (g) => groupValueToLabel[g] ?? g,
                    ),
                );
            }
        },
    );

    setSelectedUserGroups([]);
    setRecipients([]);
}, [user?.email, activeId, request_info_from_sponsor]);

	const [fileIds, setFileIds] = useState<string[]>([]);
	const [fileLoading, setFileLoading] = useState(false);
	const filenames = useSelector((state: ESRState) => state.filenames);

	const handleAttachedFileDeletion = async (fileId: string) => {
		if (!user) return;
		try {
			await dispatch(deleteAttachedFile(esrId, fileId, user.email));
			setFileIds((prev) => prev.filter((id) => id !== fileId));
		} catch (e) {
			console.error("Delete failed", e);
		}
	};

	const uploadFiles = async (e: ChangeEvent<HTMLInputElement>) => {
		if (!user) return;
		if (!e.target.files) return;
		try {
			setFileLoading(true);
			setFileIds([]);
			const responses = await Promise.all(
				Array.from(e.target.files).map((file) =>
					request.postForm<{ file_id: string }>(
						getBackendURL("/esr/uploadfile/"),
						{
							esr_id: esrId,
							responder_id: user.email,
							addendum_label: activeLabel,
							question_id:
								filters.questionId !== undefined ? filters.questionId : "",
							file,
						},
					),
				),
			);
			const ids = responses.map((res) => res.data.file_id);
			for (const fileId of ids) {
				dispatch(loadFileInfo(activeId, fileId));
			}
			setFileIds(ids);
			e.target.value = "";
			setFileLoading(false);
		} catch (e) {
			console.error("Error UI *NewThreadEntry* <uploadFiles>", e);
		}
	};

	const enableCreateBtn = description && recipients;
	const saveMessage = async () => {
		if (!user || !activeId) return;
		if (user && activeId) {
			dispatch(
				createMessage(
					activeId,
					description,
					recipients,
					selected_user_groups,
					message,
					filters.questionId,
					fileIds,
				),
			);
		}
		if (request_info_from_sponsor) {
			dispatch<DataAction>({
				type: "REQUEST_INFO_FROM_SPONSOR_COMPLETE",
			});
		}
		setDescription("");
		setMessage("");
		setRecipients([]);
		setSelectedUserGroups([]);
		setFileIds([]);
	};

	// Temporary workaround to silence WAVE
	const containerRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		containerRef.current?.querySelectorAll("textarea").forEach((el) => {
			el.setAttribute("id", "problem-description");
		});
	});

	return (
		<Box
			ref={containerRef}
			sx={{
				boxShadow: "0px 0px 6px 2px #003E621F",
				px: "24px",
				"&  .MuiInput-root": {
					height: 54,
				},
				background: "white",
			}}
		>
			<TextareaAutosize
				id="problem-description"
				placeholder="Enter your thread title"
				value={description}
				style={{
					margin: "16px 0",
					width: "100%",
					resize: "none",
					fontSize: "16px",
					fontFamily: "Roboto, sans-serif",
					background: "none",
					border: "none",
					outline: "none",
				}}
				onChange={(e) => setDescription(e.target.value)}
			/>
			<Divider />
			<TextareaAutosize
				id="message"
				placeholder="Enter your content"
				value={message}
				style={{
					margin: "16px 0",
					width: "100%",
					resize: "none",
					fontSize: "16px",
					fontFamily: "Roboto, sans-serif",
					background: "none",
					border: "none",
					outline: "none",
				}}
				onChange={(e) => setMessage(e.target.value)}
			/>
			<Box>
				<Stack direction="row" sx={{ my: "8px", flexWrap: "wrap", gap: "4px" }}>
					{fileIds.map((fileId) => (
						<Chip
							key={fileId}
							label={filenames?.[fileId]}
							onClick={() =>
								download(
									getBackendURL(
										`/esr/attachment/?esr_id=${esrId}&file_id=${fileId}`,
									),
								)
							}
							onDelete={() => handleAttachedFileDeletion(fileId)}
						/>
					))}
				</Stack>
				<Button
					component="label"
					variant="text"
					startIcon={
						fileLoading ? <CircularProgress size={20} /> : <UploadFile />
					}
				>
					Attach file
					<input
						type="file"
						multiple
						style={{ clipPath: "inset(50%)", height: 1, width: 1 }}
						onChange={uploadFiles}
					/>
				</Button>
			</Box>
			{enableCreateBtn ? (
				<>
					<Divider />

					<Stack
						spacing={3}
						sx={{ mt: 2, mb: 10, maxHeight: 300, overflowY: "auto" }}
					>
						{/* User Selection */}
						<UserTreeView
							selected_user_groups={selected_user_groups}
							setSelectedUserGroups={(groups) =>
								setSelectedUserGroups(
									groups.map((g) => groupLabelToValue[g] ?? g),
								)
							}
							recipients={recipients}
							setRecipients={setRecipients}
							recipientOptions={recipientOptions}
							allowed_user_groups={allowed_user_groups}
							groupOrder={groupOrder}
						/>
						{(selected_user_groups.length > 0 || recipients.length > 0) && (
							<Box sx={{ mt: 2 }}>
								{selected_user_groups.length > 0 && (
									<>
										<Typography variant="subtitle2" sx={{ mb: 1 }}>
											Selected Groups:
										</Typography>
										<Stack
											direction="row"
											spacing={1}
											flexWrap="wrap"
											sx={{
												mb: 2,
												gap: 1,
												maxHeight: 220,
												overflowY: "auto",
												pr: 1,
											}}
										>
											{selected_user_groups.map((group) => (
												<Chip
													key={group}
													label={groupValueToLabel[group] ?? group}
													color="primary"
													size="small"
												/>
											))}
										</Stack>
									</>
								)}

								{recipients.length > 0 && (
									<>
										<Typography variant="subtitle2" sx={{ mb: 1 }}>
											Selected Users:
										</Typography>
										<Stack
											direction="row"
											spacing={1}
											flexWrap="wrap"
											sx={{
												mb: 2,
												gap: 1,
												maxHeight: 220,
												overflowY: "auto",
												pr: 1,
											}}
										>
											{recipients.map((email, index) => (
												<Chip
													key={`${email} - ${index} - list`}
													label={email}
													color="secondary"
													size="small"
												/>
											))}
										</Stack>
									</>
								)}
							</Box>
						)}
						{/* Send Button */}
						<Button
							size="small"
							variant="contained"
							onClick={saveMessage}
							disabled={
								(recipients.length === 0 &&
									selected_user_groups.length === 0) ||
								description.length === 0 ||
								message.length === 0
							}
							sx={{
								position: "sticky",
								bottom: 0,
								zIndex: 1,
								mt: 1,
								mb: 1,
							}}
						>
							Send
						</Button>
					</Stack>
				</>
			) : null}
		</Box>
	);
};
